import type { RoomConfiguration } from '@components/RoomConfigurator';
import {
  PACKAGE_STORAGE,
  SELECTED_FLIGHTS_STORAGE,
  SELECTED_HOTEL_STORAGE,
  SELECTED_ROOMS_STORAGE,
  SELECTED_TICKET_STORAGE,
} from '@config/storageKeys';

export type RoomDraft = Omit<RoomConfiguration, 'children'> & {
  children: (number | null)[];
};
type StorageReader = Pick<Storage, 'getItem'>;

export function asRecord(value: unknown): Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
    ? Object.fromEntries(Object.entries(value))
    : {};
}

export function readHotelStorage(storage: StorageReader, key: string) {
  try {
    return asRecord(JSON.parse(storage.getItem(key) || '{}'));
  } catch {
    return {};
  }
}

const isCount = (value: unknown): value is number =>
  typeof value === 'number' && Number.isSafeInteger(value) && value >= 0;

function readRoomDrafts(value: unknown): RoomDraft[] | null {
  if (!Array.isArray(value) || !value.length || value.length > 8) return null;
  const rooms: RoomDraft[] = [];
  for (const entry of value) {
    const room = asRecord(entry);
    const infants = room.infants ?? 0;
    if (
      !isCount(room.adults) ||
      room.adults < 1 ||
      !isCount(infants) ||
      !Array.isArray(room.children)
    )
      return null;
    rooms.push({
      adults: room.adults,
      infants,
      children: room.children.map((age) =>
        isCount(age) && age >= 2 && age <= 17 ? age : null
      ),
    });
  }
  return rooms;
}

export function confirmedRooms(rooms: RoomDraft[]): RoomConfiguration[] | null {
  if (!rooms.length || rooms.length > 8) return null;
  const result: RoomConfiguration[] = [];
  for (const room of rooms) {
    if (!isCount(room.adults) || room.adults < 1 || !isCount(room.infants))
      return null;
    const children: number[] = [];
    for (const age of room.children) {
      if (!isCount(age) || age < 2 || age > 17) return null;
      children.push(age);
    }
    result.push({ ...room, children });
  }
  return result;
}

export function occupancyKey(value: RoomConfiguration[]) {
  return JSON.stringify(
    value.map((room) => [room.adults, room.children, room.infants ?? 0])
  );
}

export function suggestRooms(
  adults: number,
  children: number,
  infants: number
): RoomDraft[] {
  const roomCount = Math.min(8, Math.ceil(adults / 2));
  const rooms: RoomDraft[] = Array.from({ length: roomCount }, (_, index) => ({
    adults:
      Math.floor(adults / roomCount) + (index < adults % roomCount ? 1 : 0),
    children: [],
    infants: 0,
  }));
  const guestCount = (room: RoomDraft) =>
    room.adults + room.children.length + room.infants;
  for (let index = 0; index < children + infants; index++) {
    const room = rooms.reduce((least, next) =>
      guestCount(next) < guestCount(least) ? next : least
    );
    if (index < children) room.children.push(null);
    else room.infants++;
  }
  return rooms;
}

export function getRoomPrefill({
  storage,
  eventId,
  rooms,
  tickets,
}: {
  storage: StorageReader;
  eventId: string;
  rooms: RoomConfiguration[];
  tickets: number;
}) {
  const packageData = readHotelStorage(storage, PACKAGE_STORAGE);
  const selected = asRecord(packageData.selected);
  const passengers = asRecord(
    readHotelStorage(storage, SELECTED_FLIGHTS_STORAGE).passengers
  );
  const ticketQuantity = readHotelStorage(
    storage,
    SELECTED_TICKET_STORAGE
  ).quantity;
  const packageTickets =
    isCount(packageData.tickets) && packageData.tickets > 0
      ? packageData.tickets
      : tickets;
  const quantity =
    selected.tickets !== false && isCount(ticketQuantity) && ticketQuantity > 0
      ? ticketQuantity
      : packageTickets;
  let source: 'flights' | 'tickets' | 'existing' = 'existing';
  let counts = { adults: 2, children: 0, infants: 0 };
  if (
    selected.flights !== false &&
    isCount(passengers.adults) &&
    passengers.adults > 0 &&
    isCount(passengers.children) &&
    isCount(passengers.infants)
  ) {
    source = 'flights';
    counts = {
      adults: passengers.adults,
      children: passengers.children,
      infants: passengers.infants,
    };
  } else if (isCount(quantity) && quantity > 0) {
    source = 'tickets';
    counts.adults = quantity;
  }
  const sourceKey = JSON.stringify([
    eventId,
    source,
    counts.adults,
    counts.children,
    counts.infants,
  ]);
  const storedRooms = readRoomDrafts(packageData.roomConfigurations);
  const existing = readRoomDrafts(rooms) ?? storedRooms;
  const savedConfirmed = Boolean(storedRooms && confirmedRooms(storedRooms));
  const matchesCounts =
    existing &&
    existing.reduce((sum, room) => sum + room.adults, 0) === counts.adults &&
    existing.reduce((sum, room) => sum + room.children.length, 0) ===
      counts.children &&
    existing.reduce((sum, room) => sum + room.infants, 0) === counts.infants;
  const isConfirmed = Boolean(
    existing &&
    confirmedRooms(existing) &&
    (packageData.hotelRoomSourceKey === sourceKey ||
      (!packageData.hotelRoomSourceKey &&
        (savedConfirmed ||
          storage.getItem(SELECTED_HOTEL_STORAGE) ||
          storage.getItem(SELECTED_ROOMS_STORAGE))))
  );
  return {
    sourceKey,
    source,
    isConfirmed,
    rooms:
      existing && (isConfirmed || matchesCounts || source === 'existing')
        ? existing
        : suggestRooms(counts.adults, counts.children, counts.infants),
  };
}

export function saveRoomConfiguration({
  storage,
  rooms,
  previousRooms,
  sourceKey,
}: {
  storage: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;
  rooms: RoomConfiguration[];
  previousRooms: RoomConfiguration[];
  sourceKey: string;
}) {
  const packageData = readHotelStorage(storage, PACKAGE_STORAGE);
  const changed = occupancyKey(previousRooms) !== occupancyKey(rooms);
  storage.setItem(
    PACKAGE_STORAGE,
    JSON.stringify({
      ...packageData,
      roomConfigurations: rooms,
      hotelRoomSourceKey: sourceKey,
      ...(changed
        ? { selected: { ...asRecord(packageData.selected), hotel: false } }
        : {}),
    })
  );
  if (changed) {
    storage.removeItem(SELECTED_HOTEL_STORAGE);
    storage.removeItem(SELECTED_ROOMS_STORAGE);
  }
  return changed;
}
